import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material';

export interface Toolkit {
  id: string;
  code: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  price: {
    monthly: number;
    annual: number;
  };
  features: string[];
  agents: string[];
  compliance?: string[];
  available: boolean;
  comingSoon?: boolean;
}

const toolkits: Toolkit[] = [
  {
    id: 'mep_toolkit',
    code: 'MEP_TOOLKIT',
    name: 'MEP Engineering',
    icon: <EngineeringIcon style={{ fontSize: 32 }} />,
    description: 'Professional Mechanical, Electrical, and Plumbing toolkit for engineers',
    price: {
      monthly: 129,
      annual: 1290,
    },
    features: [
      'HVAC load calculations (ASHRAE)',
      'Electrical load analysis (NEC)',
      'CAD/DXF file processing',
      'Climate data integration',
      'Room extraction & analysis',
      'Equipment sizing recommendations',
    ],
    agents: ['CADex', 'ThermaCalc', 'VoltMaster', 'SyncMEP', 'CodeGuard'],
    compliance: ['ASHRAE', 'NEC 2020', 'IBC', 'IECC'],
    available: true,
  },
];

interface ToolkitSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (toolkit: Toolkit) => void;
  billingFrequency: 'monthly' | 'annual';
  selectedToolkitId?: string;
}

export const ToolkitSelectionDialog: React.FC<ToolkitSelectionDialogProps> = ({
  open,
  onClose,
  onSelect,
  billingFrequency,
  selectedToolkitId,
}) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(selectedToolkitId);

  const handleSelect = (toolkit: Toolkit) => {
    if (!toolkit.available) return;
    setSelectedId(toolkit.id);
  };

  const handleConfirm = () => {
    const selected = toolkits.find(t => t.id === selectedId);
    if (selected && selected.available) {
      onSelect(selected);
      onClose();
    }
  };

  const calculatePrice = (toolkit: Toolkit) => {
    const basePrice = toolkit.price[billingFrequency];
    if (billingFrequency === 'annual') {
      return {
        total: basePrice,
        monthly: Math.round(basePrice / 12),
        savings: toolkit.price.monthly * 12 - basePrice,
      };
    }
    return {
      total: basePrice,
      monthly: basePrice,
      savings: 0,
    };
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" fontWeight="bold">
            Select Your Industry Toolkit
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" style={{ marginBottom: 24 }}>
          Industry-specific toolkits include specialized AI agents and professional tools tailored for your field.
          Each toolkit adds to your base subscription price.
        </Alert>

        <Grid container spacing={3}>
          {toolkits.map((toolkit) => {
            const pricing = calculatePrice(toolkit);
            const isSelected = selectedId === toolkit.id;
            const isDisabled = !toolkit.available;

            return (
              <Grid item xs={12} md={6} lg={4} key={toolkit.id}>
                <Card
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: isSelected ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.12)',
                    opacity: isDisabled ? 0.7 : 1,
                    position: 'relative',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => handleSelect(toolkit)}
                >
                  {toolkit.comingSoon && (
                    <Chip
                      label="COMING SOON"
                      color="warning"
                      size="small"
                      style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 1,
                      }}
                    />
                  )}

                  <CardContent style={{ flex: 1 }}>
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <div style={{ color: isDisabled ? 'rgba(0,0,0,0.38)' : '#1976d2', marginBottom: 8 }}>
                        {toolkit.icon}
                      </div>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {toolkit.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
                        {toolkit.description}
                      </Typography>
                      <div>
                        <Typography variant="h4" component="span" fontWeight="bold">
                          ${pricing.monthly}
                        </Typography>
                        <Typography variant="body1" component="span" color="text.secondary">
                          /mo
                        </Typography>
                        {billingFrequency === 'annual' && pricing.savings > 0 && (
                          <Typography variant="body2" color="success.main">
                            Save ${pricing.savings}/yr
                          </Typography>
                        )}
                      </div>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Features:
                      </Typography>
                      <List dense style={{ paddingTop: 0, paddingBottom: 0 }}>
                        {toolkit.features.slice(0, 4).map((feature, index) => (
                          <ListItem key={index} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 2, paddingBottom: 2 }}>
                            <ListItemIcon style={{ minWidth: 28 }}>
                              <CheckIcon color={isDisabled ? 'disabled' : 'success'} style={{ fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                color: isDisabled ? 'text.disabled' : 'text.primary',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Specialized Agents:
                      </Typography>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {toolkit.agents.map((agent) => (
                          <Chip
                            key={agent}
                            label={agent}
                            size="small"
                            color={isDisabled ? 'default' : 'secondary'}
                            variant="outlined"
                            style={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </div>
                    </div>

                    {toolkit.compliance && (
                      <div>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Compliance:
                        </Typography>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {toolkit.compliance.map((standard) => (
                            <Chip
                              key={standard}
                              label={standard}
                              size="small"
                              variant="filled"
                              style={{
                                fontSize: '0.7rem',
                                backgroundColor: isDisabled ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.08)',
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>

                  <CardActions style={{ padding: 16, paddingTop: 0 }}>
                    <Button
                      fullWidth
                      variant={isSelected ? 'contained' : 'outlined'}
                      color="primary"
                      disabled={isDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(toolkit);
                      }}
                    >
                      {isDisabled ? 'Coming Soon' : (isSelected ? 'Selected' : 'Select')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 24 }}>
        <Button onClick={onClose} size="large">
          Cancel
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleConfirm}
          disabled={!selectedId || !toolkits.find(t => t.id === selectedId)?.available}
          style={{ minWidth: 120 }}
        >
          Confirm Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToolkitSelectionDialog;